import { useEffect, useState } from "react";
import { ASSETS, LINKS } from "@/lib/brand";
import { ArrowUpRight, Menu, X } from "lucide-react";

const NAV_LINKS = [
  { label: "Services", href: LINKS.services, id: "services" },
  { label: "Audit", href: LINKS.audit, id: "audit" },
  { label: "Process", href: LINKS.process, id: "process" },
  { label: "Use Cases", href: LINKS.useCases, id: "use-cases" },
  { label: "Contact", href: LINKS.contact, id: "contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [hideBrandWord, setHideBrandWord] = useState(false);

  useEffect(() => {
    const updateBrandWordVisibility = () => {
      const hero = document.getElementById("top");
      if (!hero) {
        setHideBrandWord(false);
        return;
      }

      const rect = hero.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), Math.max(total, 1));
      const progress = total > 0 ? scrolled / total : 0;

      const shouldHide = progress >= 0.82 && rect.bottom > window.innerHeight;
      setHideBrandWord((current) =>
        current === shouldHide ? current : shouldHide
      );
    };

    updateBrandWordVisibility();
    window.addEventListener("scroll", updateBrandWordVisibility, {
      passive: true,
    });
    window.addEventListener("resize", updateBrandWordVisibility);

    return () => {
      window.removeEventListener("scroll", updateBrandWordVisibility);
      window.removeEventListener("resize", updateBrandWordVisibility);
    };
  }, []);

  // Close mobile menu on hash change or escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <>
      <header className="top-nav" data-testid="top-nav">
        <a className="nav-brand" href="#top" data-testid="nav-brand">
          <img
            src={ASSETS.logoLight}
            alt="ForgeLayer AI"
            loading="eager"
            decoding="async"
            style={{ height: 38, width: 38, objectFit: "contain" }}
            data-testid="nav-brand-logo"
          />
          <span className={`brand-word${hideBrandWord ? " is-hidden" : ""}`}>
            FORGELAYER<span className="ai">AI</span>
          </span>
        </a>

        <nav className="nav-pill" aria-label="Primary" data-testid="nav-pill">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-nav-link
              data-nav-target={l.id}
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <a
            className="nav-cta"
            href={LINKS.audit}
            aria-label="Get Free Audit"
            data-testid="nav-cta-audit"
          >
            <span className="label">Get Free Audit</span>
            <span className="arrow" aria-hidden="true">
              <ArrowUpRight size={13} strokeWidth={2.2} />
            </span>
          </a>
          <button
            type="button"
            className="nav-burger"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-burger"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </header>

      {/* Mobile menu sheet */}
      <div
        id="mobile-menu"
        className={`mobile-menu${open ? " is-open" : ""}`}
        aria-hidden={!open}
        data-testid="mobile-menu"
      >
        <nav className="mobile-menu-inner">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={l.href}
              onClick={close}
              data-testid={`mobile-nav-link-${l.id}`}
            >
              {l.label}
            </a>
          ))}
          <a
            href={LINKS.audit}
            onClick={close}
            className="btn-primary"
            style={{ marginTop: 24, alignSelf: "flex-start" }}
            data-testid="mobile-nav-cta"
          >
            Get My Free AI Audit
            <span className="btn-arrow" aria-hidden="true">
              <ArrowUpRight size={13} strokeWidth={2.2} />
            </span>
          </a>
        </nav>
      </div>
    </>
  );
}
